import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Typography, Steps, Card, Form, Checkbox, message, Alert } from 'antd';
import { InfoCircleOutlined, CheckCircleOutlined, FileTextOutlined, LoadingOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const ContractSelectionModal = ({ visible, onClose, onContractSelected, trialStatus }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedContract, setSelectedContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [contractContent, setContractContent] = useState('');
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [hasActiveCommission, setHasActiveCommission] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Charger les plans d'abonnement disponibles et vérifier les contrats actifs
  useEffect(() => {
    if (visible) {
      loadSubscriptionPlans();
      checkActiveContracts();
    }
  }, [visible, user?.id]);

  // Vérifier les contrats actifs
  const checkActiveContracts = async () => {
    if (!user?.id) return;

    try {
      const [subscriptionRes, commissionRes] = await Promise.all([
        api.getActiveSubscription(user.id).catch(e => {
          console.error('Erreur lors de la vérification de l\'abonnement:', e);
          return { data: null };
        }),
        api.getActiveCommission(user.id).catch(e => {
          console.error('Erreur lors de la vérification de la commission:', e);
          return { data: null };
        })
      ]);

      const subscription = subscriptionRes?.data?.data || subscriptionRes?.data || null;
      const commission = commissionRes?.data?.data || commissionRes?.data || null;

      setHasActiveSubscription(!!subscription);
      setHasActiveCommission(!!commission);
    } catch (error) {
      console.error('Erreur lors de la vérification des contrats:', error);
    }
  };

  const loadSubscriptionPlans = async () => {
    try {
      setIsLoadingPlans(true);
      const response = await api.get('/subscription-plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des plans:', error);
      message.error('Impossible de charger les plans d\'abonnement');
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const contractTypes = [
    {
      key: 'subscription',
      title: 'Abonnement Mensuel',
      description: 'Accès illimité à toutes les fonctionnalités',
      features: [
        'Accès à toutes les missions',
        'Paiements sécurisés',
        'Support prioritaire',
        'Facturation mensuelle récurrente',
        'Aucune commission sur les missions'
      ]
    },
    {
      key: 'commission',
      title: 'Commission par Mission',
      description: 'Payez uniquement lorsque vous travaillez',
      features: [
        '10% de commission sur chaque mission',
        'Paiement après réalisation',
        'Aucun engagement',
        'Flexibilité maximale',
        'Aucun frais mensuel'
      ]
    }
  ];

  const handleContractSelect = (contractType) => {
    setSelectedContract(contractType);
    
    if (contractType === 'subscription' && plans.length > 0) {
      // Pour l'abonnement, on doit d'abord sélectionner un plan
      setCurrentStep(1);
    } else {
      // Pour la commission, on passe directement à l'étape de confirmation
      setCurrentStep(2);
    }
    
    // Générer le contenu du contrat en fonction du type
    generateContractContent(contractType);
  };

  const generateContractContent = (contractType) => {
    if (contractType === 'subscription') {
      setContractContent(`CONTRAT D'ABONNEMENT

Ce contrat régit les conditions d'utilisation de nos services en tant qu'abonné.

Engagements :
- Paiement mensuel récurrent
- Accès illimité à toutes les fonctionnalités
- Support prioritaire
- Aucune commission sur les missions

Prix : À partir de 29,99€/mois
Durée : Engagement mensuel résiliable à tout moment`);
    } else {
      setContractContent(`CONTRAT DE COMMISSION

Ce contrat régit les conditions d'utilisation de nos services en mode commission.

Conditions :
- 10% de commission sur le montant de chaque mission
- Paiement après réalisation de la mission
- Aucun engagement de durée
- Aucun frais mensuel

Le paiement de la commission est effectué après validation de chaque mission.`);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setCurrentStep(2);
    generateContractContent('subscription', plan);
  };

  const handleSubmit = async () => {
    if (!agreedToTerms) {
      message.error('Veuillez accepter les conditions du contrat pour continuer');
      return;
    }

    if (selectedContract === 'subscription' && !selectedPlan) {
      message.error('Veuillez sélectionner un plan d\'abonnement');
      return;
    }

    setLoading(true);
    try {
      // Créer un nouveau contrat
      const contractData = {
        type: selectedContract,
        status: 'pending',
        ...(selectedContract === 'subscription' && {
          subscriptionPlanId: selectedPlan.plan_id,
          startDate: new Date(),
          endDate: null,
          terms: 'Contrat d\'abonnement mensuel'
        }),
        ...(selectedContract === 'commission' && {
          commissionRate: 10, // 10% de commission
          fixedAmount: 0,
          startDate: new Date(),
          terms: 'Contrat de commission par mission'
        })
      };

      const response = await api.post('/contracts', contractData);
      
      message.success('Contrat créé avec succès !');
      
      // Rediriger vers la page de paiement si c'est un abonnement
      if (selectedContract === 'subscription') {
        navigate(`/demenageur/contrat/abonnement?newContract=${response.data.id}`);
      } else {
        // Pour les commissions, on reste sur la page actuelle
        navigate('/demenageur/contrat/commission');
      }
      
      onContractSelected(selectedContract);
      onClose();
      
    } catch (error) {
      console.error('Erreur lors de la création du contrat:', error);
      message.error(error.response?.data?.message || 'Une erreur est survenue lors de la création du contrat');
    } finally {
      setLoading(false);
    }
  };

  const renderContractTypeSelection = useCallback(() => {
    // Filtrer les types de contrats disponibles en fonction des contrats actifs
    const availableContracts = contractTypes.filter(contract => {
      // Masquer l'abonnement si l'utilisateur a déjà un contrat de commission actif
      if (contract.key === 'subscription' && hasActiveCommission) {
        return false;
      }
      // Masquer la commission si l'utilisateur a déjà un abonnement actif
      if (contract.key === 'commission' && hasActiveSubscription) {
        return false;
      }
      return true;
    });

    // Si aucun contrat n'est disponible, afficher un message
    if (availableContracts.length === 0) {
      return (
        <Alert
          message="Vous avez déjà un contrat actif"
          description="Vous ne pouvez pas choisir un autre type de contrat tant que votre contrat actuel est actif."
          type="info"
          showIcon
          className="mt-6"
        />
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {availableContracts.map((contract) => (
          <Card
            key={contract.key}
            hoverable
            className={`border-2 ${selectedContract === contract.key ? 'border-blue-500' : 'hover:border-gray-300'}`}
            onClick={() => handleContractSelect(contract.key)}
          >
            <div className="text-center">
              <Title level={4} className="mb-2">{contract.title}</Title>
              <Text strong className="text-xl text-blue-600">
                {contract.key === 'subscription' ? 'À partir de 29,99€/mois' : '10% par mission'}
              </Text>
              <Paragraph className="text-gray-600 mt-2">{contract.description}</Paragraph>
              <div className="mt-4 text-left">
                {contract.features.map((feature, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <CheckCircleOutlined className="text-green-500 mr-2" />
                    <Text>{feature}</Text>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }, [selectedContract, hasActiveSubscription, hasActiveCommission]);

  const renderPlanSelection = useCallback(() => {
    if (isLoadingPlans) {
      return (
        <div className="flex justify-center items-center h-64">
          <LoadingOutlined style={{ fontSize: 32 }} />
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {plans.map((plan) => (
          <Card
            key={plan.plan_id}
            hoverable
            className={`border-2 ${selectedPlan?.plan_id === plan.plan_id ? 'border-blue-500' : 'hover:border-gray-300'}`}
            onClick={() => handlePlanSelect(plan)}
          >
            <div className="text-center">
              <Title level={4} className="mb-2">{plan.name}</Title>
              <Text strong className="text-2xl text-blue-600 block mb-2">
                {plan.price} {plan.currency}
              </Text>
              <Text type="secondary" className="block mb-4">
                {plan.billing_cycle === 'monthly' ? 'par mois' : 'par an'}
              </Text>
              <Paragraph className="text-gray-600 mb-4">{plan.description}</Paragraph>
              <div className="text-left">
                {plan.features?.map((feature, index) => (
                  <div key={index} className="flex items-center mb-2">
                    <CheckCircleOutlined className="text-green-500 mr-2" />
                    <Text>{feature}</Text>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }, [isLoadingPlans, plans, selectedPlan]);

  const renderConfirmation = useCallback(() => (
    <div className="mt-6">
      <div className="bg-gray-50 p-6 rounded-lg mb-6">
        <div className="flex items-center mb-4">
          <FileTextOutlined className="text-blue-500 text-xl mr-2" />
          <div>
            <Title level={4} className="mb-0">
              {selectedContract === 'subscription' 
                ? `Contrat d'abonnement - ${selectedPlan?.name || ''}`
                : 'Contrat de commission par mission'}
            </Title>
            {selectedContract === 'subscription' && selectedPlan && (
              <Text type="secondary">
                {selectedPlan.price} {selectedPlan.currency} / {selectedPlan.billing_cycle === 'monthly' ? 'mois' : 'an'}
              </Text>
            )}
            {selectedContract === 'commission' && (
              <Text type="secondary">10% de commission sur chaque mission</Text>
            )}
          </div>
        </div>
        
        <div className="bg-white p-4 rounded border border-gray-200 h-64 overflow-y-auto mb-4">
          <pre className="whitespace-pre-wrap font-sans">{contractContent}</pre>
        </div>
        
        <div className="mt-4">
          <Checkbox 
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="text-gray-700"
          >
            Je reconnais avoir pris connaissance et accepte les termes du contrat ci-dessus
          </Checkbox>
        </div>
      </div>
      
      <div className="flex justify-between mt-6">
        <Button onClick={() => setCurrentStep(selectedContract === 'subscription' ? 1 : 0)}>
          Retour
        </Button>
        <Button 
          type="primary" 
          onClick={handleSubmit}
          loading={loading}
          disabled={!agreedToTerms}
          size="large"
          className="bg-blue-600 hover:bg-blue-700"
        >
          {selectedContract === 'subscription' ? 'Payer maintenant' : 'Confirmer le contrat'}
        </Button>
      </div>
    </div>
  ), [selectedContract, selectedPlan, contractContent, agreedToTerms, loading]);

  // Get the current step content based on the current step
  const getStepContent = () => {
    if (currentStep === 0) {
      return renderContractTypeSelection();
    }
    
    if (currentStep === 1 && selectedContract === 'subscription') {
      return renderPlanSelection();
    }
    
    // If we get here, we're either on the confirmation step or the only step for commission
    return renderConfirmation();
  };

  // Get the step titles for the stepper
  const stepTitles = [
    'Type de contrat',
    ...(selectedContract === 'subscription' ? ['Choisir un plan'] : []),
    'Confirmation'
  ];

  return (
    <Modal
      title="Sélectionnez votre type de contrat"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      closable={false}
      maskClosable={false}
    >
      <div className="mb-6">
        {trialStatus?.code === 'TRIAL_EXPIRED' ? (
          <Alert
            message="Période d'essai terminée"
            description="Votre période d'essai est terminée. Veuillez choisir un type de contrat pour continuer à utiliser nos services."
            type="warning"
            showIcon
            className="mb-6"
          />
        ) : (
          <Alert
            message="Période d'essai en cours"
            description={`Votre période d'essai se termine dans ${trialStatus?.daysRemaining || 0} jours. Vous pouvez choisir un type de contrat dès maintenant.`}
            type="info"
            showIcon
            className="mb-6"
          />
        )}
        
        <Steps current={currentStep} className="mb-8" responsive={false}>
          {stepTitles.map((title, index) => (
            <Step 
              key={title} 
              title={<span className={currentStep >= index ? 'text-blue-600' : 'text-gray-400'}>{title}</span>} 
            />
          ))}
        </Steps>
        
        <div className="steps-content">
          {getStepContent()}
        </div>
      </div>
    </Modal>
  );
};

export default ContractSelectionModal;
